import React, { useContext, useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Field, Form } from 'react-final-form';
import { parseISO } from 'date-fns';
import { Stack } from '@mui/material';
import SupervisorFields from 'components/supervisor/SupervisorFields';
import FormSurface from 'components/shared/surfaces/FormSurface';
import PremiumCard from 'components/shared/surfaces/PremiumCard';
import ProfilePictureFieldAdapter from 'components/shared/field-adapters/ProfilePictureFieldAdapter';
import PrimaryLoadingButton from 'components/shared/buttons/PrimaryLoadingButton';
import SEOPageTitle from 'components/shared/SEOPageTitle';
import requestStatus from 'common/constants/status';
import getDataToSave from 'common/form/dataToSave';
import { generatePassword, getFieldsToUpdate } from 'common/form/utils';
import validateEditSupervisorForm from 'common/validations/form/editSupervisor';
import StyledButtonsContainer from 'common/theme/containers/StyledButtonsContainer';
import StyledProfileButtonContainer from 'common/theme/profile/StyledProfileButtonContainer';
import StyledProfilePictureContainer from 'common/theme/profile/StyledProfilePictureContainer';
import StyledProfileUsername from 'common/theme/profile/StyledProfileUsername';
import AppBarContext from 'context/AppBarContext';
import authSlice from 'store/modules/auth';

const requiredFields = ['first_name', 'last_name', 'email', 'birthdate', 'ui_language'];

/**
 * Perfil editable del SUPERVISOR (como el de un cliente/usuario): foto arriba y sus datos abajo.
 * Guarda con `updateMe` (el backend ya permite que un usuario edite su propio registro).
 */
function SupervisorProfile() {
  const dispatch = useDispatch();
  const { t } = useTranslation(['buttons', 'labels', 'routes']);
  const pageTitle = t('profile', { ns: 'routes' });
  const [initialValues, setInitialValues] = useState({});
  const { setAppBarButton, setAppBarTitle } = useContext(AppBarContext);

  const status = useSelector((state) => state.auth.status);
  const user = useSelector((state) => state.auth.user);

  useEffect(() => {
    if (user && Object.keys(user).length > 0) {
      // Valores iniciales con guardas: el usuario de auth puede no traer birthdate/settings aún.
      setInitialValues({
        first_name: user.first_name,
        last_name: user.last_name,
        email: user.email,
        gender: user.gender,
        birthdate: user.birthdate ? parseISO(user.birthdate) : null,
        ui_language: user.settings?.language ?? '',
        password: null,
        photo: user.photo
      });
    }
  }, [user]);

  useEffect(() => {
    setAppBarButton(null);
    setAppBarTitle(null);
  }, [setAppBarButton, setAppBarTitle]);

  const onUpdate = (values) => {
    const fieldsToUpdate = getFieldsToUpdate(values, initialValues);
    const dataToSave = getDataToSave(fieldsToUpdate, 'user');
    dispatch(authSlice.actions.updateMe({ id: user.id, input: dataToSave }));
  };

  return (
    <>
      <SEOPageTitle title={pageTitle} />
      <Form
        initialValues={initialValues}
        mutators={{ generatePassword }}
        onSubmit={onUpdate}
        validate={validateEditSupervisorForm}
        render={({ form, handleSubmit, invalid, pristine }) => (
          <form onSubmit={handleSubmit}>
            <PremiumCard sx={{ mb: { xs: 2, lg: 4 } }} padding={{ xs: 3, md: 4 }}>
              <Stack alignItems="center" spacing={1.5} sx={{ textAlign: 'center' }}>
                <StyledProfilePictureContainer>
                  <Field
                    id="photo"
                    name="photo"
                    component={ProfilePictureFieldAdapter}
                    firstName={user.first_name}
                    lastName={user.last_name}
                  />
                </StyledProfilePictureContainer>
                <StyledProfileUsername>{user.full_name}</StyledProfileUsername>
              </Stack>
            </PremiumCard>
            <FormSurface>
              <SupervisorFields form={form} requiredFields={requiredFields} />
            </FormSurface>
            <StyledProfileButtonContainer>
              <StyledButtonsContainer>
                <PrimaryLoadingButton
                  disabled={invalid || pristine || status === requestStatus.loading}
                  loading={status === requestStatus.loading}
                  label={t('save', { ns: 'buttons' })}
                  type="submit"
                />
              </StyledButtonsContainer>
            </StyledProfileButtonContainer>
          </form>
        )}
      />
    </>
  );
}

export default SupervisorProfile;
